import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  FileText,
  Search,
  Mail,
  Phone,
  Building2,
  Calendar,
  Euro,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  ExternalLink,
  Loader2,
  ChevronRight,
  Eye,
  Send,
  ArrowUpRight,
  MoreHorizontal,
  Download,
  Printer
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';


const STATUS_CONFIG = {
  new: { label: 'Nouveau', color: 'bg-amber-100 text-amber-700', icon: Clock },
  contacted: { label: 'Contacté', color: 'bg-blue-100 text-blue-700', icon: MessageSquare },
  estimated: { label: 'Estimé', color: 'bg-indigo-100 text-indigo-700', icon: FileText },
  proposal_sent: { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-700', icon: Send },
  accepted: { label: 'Accepté', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  converted: { label: 'Converti en projet', color: 'bg-emerald-100 text-emerald-700', icon: ArrowUpRight },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const QuotesManagementPage = () => {
  const { } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchQuotes();
  }, [filterStatus]);

  const fetchQuotes = async () => {
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await api.get('/website/quote-requests${params}');
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedQuote || !newStatus) return;
    
    setActionLoading(true);
    try {
      await axios.put(`${API}/website/quote-requests/${selectedQuote.id}`, {
        status: newStatus,
        notes: notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour !');
      setShowDetailModal(false);
      setSelectedQuote(null);
      setNewStatus('');
      setNotes('');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToProject = async (quote) => {
    if (!quote) return;
    
    const confirm = window.confirm(
      `Convertir la demande de ${quote.first_name} ${quote.last_name} en projet ?\n\nCela créera un nouveau projet et marquera cette demande comme "Convertie".`
    );
    if (!confirm) return;

    setActionLoading(true);
    try {
      // Create a new client if doesn't exist
      const clientData = {
        first_name: quote.first_name || '',
        last_name: quote.last_name || '',
        email: quote.email,
        phone: quote.phone || '',
        company_name: quote.company || '',
        role: 'client'
      };

      // Create project with quote data
      const projectData = {
        name: `${quote.service_type || 'Projet'} - ${quote.company || quote.first_name}`,
        project_type: mapServiceToProjectType(quote.service_type),
        description: quote.message || quote.description || '',
        status: 'draft',
        budget: quote.budget ? parseFloat(quote.budget.replace(/[^0-9.]/g, '')) : 0,
        source_quote_id: quote.id
      };

      // Call API to create client and project
      const response = await axios.post(`${API}/projects/from-quote`, {
        quote_id: quote.id,
        client_data: clientData,
        project_data: projectData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Projet créé avec succès !');
      
      // Update quote status to converted
      await axios.put(`${API}/website/quote-requests/${quote.id}`, {
        status: 'converted',
        notes: `Converti en projet le ${new Date().toLocaleDateString('fr-FR')}`,
        converted_project_id: response.data?.project_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowDetailModal(false);
      fetchQuotes();
      
      // Navigate to the new project
      if (response.data?.project_id) {
        navigate(`/projects/${response.data.project_id}`);
      }
    } catch (error) {
      console.error('Error converting to project:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setActionLoading(false);
    }
  };

  const mapServiceToProjectType = (service) => {
    const mapping = {
      'logo': 'logo',
      'site_web': 'website',
      'website': 'website',
      'video': 'video',
      'application': 'mobile_app',
      'mobile': 'mobile_app',
      'design': 'visual',
      'visuel': 'visual'
    };
    return mapping[service?.toLowerCase()] || 'other';
  };

  // Generate Quote PDF
  const generateQuotePDF = (quote) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Header with agency branding
    doc.setFillColor(26, 54, 93);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(theme?.agency?.name || 'MSC Agency', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('DEVIS', pageWidth / 2, 32, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`N° ${quote.id?.slice(0, 8).toUpperCase() || 'DRAFT'}`, pageWidth / 2, 40, { align: 'center' });
    
    yPos = 60;

    // Date and validity
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(10);
    doc.text(`Date: ${new Date(quote.created_at || Date.now()).toLocaleDateString('fr-FR')}`, 14, yPos);
    doc.text(`Valide jusqu'au: ${new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR')}`, pageWidth - 14, yPos, { align: 'right' });
    yPos += 15;

    // Client info
    doc.setTextColor(26, 54, 93);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Client', 14, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    doc.text(`${quote.first_name || ''} ${quote.last_name || ''}`, 14, yPos);
    yPos += 5;
    if (quote.company) {
      doc.text(quote.company, 14, yPos);
      yPos += 5;
    }
    doc.text(quote.email || '', 14, yPos);
    yPos += 5;
    if (quote.phone) {
      doc.text(quote.phone, 14, yPos);
      yPos += 5;
    }
    yPos += 10;

    // Service details
    doc.setTextColor(26, 54, 93);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Détail de la prestation', 14, yPos);
    yPos += 8;

    const serviceLabels = {
      'logo': 'Création de logo',
      'site_web': 'Site Internet',
      'website': 'Site Internet',
      'video': 'Production vidéo',
      'application': 'Application mobile',
      'mobile': 'Application mobile',
      'design': 'Design graphique',
      'visuel': 'Création visuelle',
      'other': 'Prestation sur mesure'
    };

    const serviceItems = [
      [serviceLabels[quote.service_type?.toLowerCase()] || quote.service_type || 'Prestation', '1', quote.budget || 'Sur devis']
    ];

    doc.autoTable({
      startY: yPos,
      head: [['Description', 'Qté', 'Prix']],
      body: serviceItems,
      theme: 'striped',
      headStyles: { fillColor: [26, 54, 93] },
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 40, halign: 'right' }
      },
      margin: { left: 14, right: 14 }
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Description / Brief
    if (quote.message || quote.description) {
      doc.setTextColor(26, 54, 93);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Description du projet', 14, yPos);
      yPos += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);
      
      const description = quote.message || quote.description || '';
      const lines = doc.splitTextToSize(description, pageWidth - 28);
      doc.text(lines, 14, yPos);
      yPos += lines.length * 5 + 15;
    }

    // Total
    doc.setFillColor(248, 250, 252);
    doc.rect(pageWidth - 80, yPos - 5, 66, 20, 'F');
    
    doc.setTextColor(26, 54, 93);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Total HT:', pageWidth - 75, yPos + 5);
    doc.text(quote.budget || 'Sur devis', pageWidth - 14, yPos + 5, { align: 'right' });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('Ce devis est valable 30 jours à compter de sa date d\'émission.', pageWidth / 2, 280, { align: 'center' });
    doc.text(`${theme?.agency?.name || 'MSC Agency'} - Tous droits réservés`, pageWidth / 2, 286, { align: 'center' });

    // Save
    const fileName = `Devis_${quote.company || quote.last_name || 'Client'}_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
    toast.success('Devis PDF téléchargé !');
  };

  // Send quote by email (mock - would need backend email service)
  const sendQuoteByEmail = async (quote) => {
    try {
      // In production, this would call a backend endpoint to send the email
      toast.success(`Devis envoyé à ${quote.email} !`);
      
      // Update status to proposal_sent
      await axios.put(`${API}/website/quote-requests/${quote.id}`, {
        status: 'proposal_sent',
        notes: `Devis envoyé par email le ${new Date().toLocaleDateString('fr-FR')}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const filteredQuotes = quotes.filter(q => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      `${q.first_name} ${q.last_name}`.toLowerCase().includes(search) ||
      q.email?.toLowerCase().includes(search) ||
      q.company?.toLowerCase().includes(search) ||
      q.service_type?.toLowerCase().includes(search)
    );
  });

  const stats = {
    new: quotes.filter(q => q.status === 'new').length,
    contacted: quotes.filter(q => q.status === 'contacted').length,
    proposal_sent: quotes.filter(q => q.status === 'proposal_sent').length,
    total: quotes.length
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="px-4 md:px-8 pb-6 md:pb-8 pt-32">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Demandes de devis
              </h1>
              <p className="text-slate-500 mt-1">
                Gérez les demandes de devis reçues depuis le site
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.new}</p>
                    <p className="text-xs text-slate-500">Nouveaux</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.contacted}</p>
                    <p className="text-xs text-slate-500">Contactés</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Send className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.proposal_sent}</p>
                    <p className="text-xs text-slate-500">Devis envoyés</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="new">Nouveaux</SelectItem>
                <SelectItem value="contacted">Contactés</SelectItem>
                <SelectItem value="proposal_sent">Devis envoyés</SelectItem>
                <SelectItem value="accepted">Acceptés</SelectItem>
                <SelectItem value="rejected">Refusés</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quotes List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredQuotes.length === 0 ? (
            <Card className="card">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Aucune demande de devis trouvée</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="card overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                      <TableHead className="text-xs font-medium">Contact</TableHead>
                      <TableHead className="text-xs font-medium">Entreprise</TableHead>
                      <TableHead className="text-xs font-medium">Service</TableHead>
                      <TableHead className="text-xs font-medium">Budget</TableHead>
                      <TableHead className="text-xs font-medium">Statut</TableHead>
                      <TableHead className="text-xs font-medium">Date</TableHead>
                      <TableHead className="text-xs font-medium text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuotes.map((quote) => {
                      const statusConfig = STATUS_CONFIG[quote.status] || STATUS_CONFIG.new;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <TableRow 
                          key={quote.id} 
                          className="cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => {
                            setSelectedQuote(quote);
                            setNewStatus(quote.status);
                            setNotes(quote.notes || '');
                            setShowDetailModal(true);
                          }}
                        >
                          <TableCell className="py-2">
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-slate-800">
                                {quote.first_name} {quote.last_name}
                              </p>
                              <p className="text-xs text-slate-400 truncate max-w-[180px]">
                                {quote.email}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="py-2">
                            <span className="text-sm text-slate-600">{quote.company || 'Particulier'}</span>
                          </TableCell>
                          <TableCell className="py-2">
                            <span className="text-sm text-slate-600">{quote.service_type || 'Projet'}</span>
                          </TableCell>
                          <TableCell className="py-2">
                            <span className="text-sm text-slate-600">{quote.budget || 'À définir'}</span>
                          </TableCell>
                          <TableCell className="py-2">
                            <Badge className={cn("text-[10px] h-5", statusConfig.color)}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2">
                            <span className="text-xs text-slate-500">
                              {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </TableCell>
                          <TableCell className="py-2 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedQuote(quote);
                                  setNewStatus(quote.status);
                                  setNotes(quote.notes || '');
                                  setShowDetailModal(true);
                                }}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Voir détails
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  generateQuotePDF(quote);
                                }}>
                                  <Download className="w-4 h-4 mr-2" />
                                  Télécharger PDF
                                </DropdownMenuItem>
                                {quote.email && (
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    sendQuoteByEmail(quote);
                                  }}>
                                    <Send className="w-4 h-4 mr-2" />
                                    Envoyer le devis
                                  </DropdownMenuItem>
                                )}
                                {quote.email && (
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`mailto:${quote.email}`, '_blank');
                                  }}>
                                    <Mail className="w-4 h-4 mr-2" />
                                    Email direct
                                  </DropdownMenuItem>
                                )}
                                {quote.phone && (
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`tel:${quote.phone}`, '_blank');
                                  }}>
                                    <Phone className="w-4 h-4 mr-2" />
                                    Appeler
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </main>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>
              Demande de devis - {selectedQuote?.first_name} {selectedQuote?.last_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs text-slate-500">Nom complet</label>
                  <p className="font-medium">{selectedQuote.first_name} {selectedQuote.last_name}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-500">Entreprise</label>
                  <p className="font-medium">{selectedQuote.company || 'Particulier'}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-500">Email</label>
                  <a href={`mailto:${selectedQuote.email}`} className="font-medium text-cyan-600 hover:underline flex items-center gap-1">
                    {selectedQuote.email}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-500">Téléphone</label>
                  {selectedQuote.phone ? (
                    <a href={`tel:${selectedQuote.phone}`} className="font-medium text-cyan-600 hover:underline">
                      {selectedQuote.phone}
                    </a>
                  ) : <p className="text-slate-400">-</p>}
                </div>
              </div>

              {/* Project Details */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <h4 className="font-semibold text-slate-800">Détails du projet</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Type de service</label>
                    <p className="font-medium">{selectedQuote.service_type || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Budget estimé</label>
                    <p className="font-medium">{selectedQuote.budget || 'À définir'}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Délai souhaité</label>
                    <p className="font-medium">{selectedQuote.timeline || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Date de demande</label>
                    <p className="font-medium">{new Date(selectedQuote.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedQuote.description && (
                <div className="space-y-2">
                  <label className="text-xs text-slate-500">Description du projet</label>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedQuote.description}</p>
                </div>
              )}

              {/* Status Update */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-semibold text-slate-800">Mettre à jour le statut</h4>
                <div className="space-y-2">
                  <Label>Nouveau statut</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Nouveau</SelectItem>
                      <SelectItem value="contacted">Contacté</SelectItem>
                      <SelectItem value="estimated">Estimé</SelectItem>
                      <SelectItem value="proposal_sent">Devis envoyé</SelectItem>
                      <SelectItem value="accepted">Accepté</SelectItem>
                      <SelectItem value="rejected">Refusé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes internes</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notes sur cette demande..."
                    rows={3}
                  />
                </div>
                
                {/* Convert to Project Button */}
                {selectedQuote?.status === 'accepted' && (
                  <div className="pt-3 border-t">
                    <Button 
                      variant="outline"
                      className="w-full bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                      onClick={() => handleConvertToProject(selectedQuote)}
                      disabled={actionLoading}
                    >
                      <ArrowUpRight className="w-4 h-4 mr-2" />
                      Convertir en projet
                    </Button>
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      Créera un nouveau projet et un compte client
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
            <Button 
              onClick={handleUpdateStatus}
              disabled={actionLoading}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Mettre à jour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesManagementPage;
